/**
 * Time-input helpers — purely functional, used by `TimeInput.tsx` and unit
 * tests. The model is "always 4 digits while focused, formatted as HH:mm",
 * with positional editing semantics on web (typing overwrites the digit at
 * the cursor; deleting zero-fills it). Hours clamp to 0–23, minutes 0–59.
 */

/** Clamp `h` and `m` to a valid time and format as "HH:mm". */
export function clampTime(h: number, m: number): string {
  const hc = Math.max(0, Math.min(23, Math.floor(h)));
  const mc = Math.max(0, Math.min(59, Math.floor(m)));
  return `${String(hc).padStart(2, '0')}:${String(mc).padStart(2, '0')}`;
}

/** Parse "HH:mm" or any partial value into raw digits (max 4). */
export function valueToDigits(value: string): string {
  return (value || '').replace(/\D/g, '').slice(0, 4);
}

/** Format raw 4-character digits as "HH:mm". Pads with trailing zeros so
 *  shorter buffers still produce a sane display. */
export function formatDigits(digits: string): string {
  const padded = (digits || '').padEnd(4, '0').slice(0, 4);
  return `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
}

/** Convert a 4-digit string to clamped "HH:mm". For shorter input pads
 *  trailing zeros (used when blur-committing partial drafts). */
export function digitsToValue(digits: string): string {
  if (digits.length === 0) return '';
  const padded = digits.padEnd(4, '0');
  return clampTime(parseInt(padded.slice(0, 2), 10), parseInt(padded.slice(2, 4), 10));
}

// ---------- Positional cursor / digit-index mapping ----------

/** Digit index that the cursor currently sits "on" — i.e. the next digit
 *  the user will overwrite by typing. Display layout is `H H : M M` with
 *  cursor positions 0-5 between characters; position 2 sits on the colon
 *  and is snapped forward to digit 2 by `typeDigitAtCursor`. */
export function digitIndexAtCursor(cursor: number): number | null {
  if (cursor <= 0) return 0;
  if (cursor === 1) return 1;
  if (cursor === 2) return null;  // colon — caller snaps forward to digit 2
  if (cursor === 3) return 2;
  if (cursor === 4) return 3;
  return null;
}

/** Cursor position to land on AFTER replacing the digit at `digitIdx`. We
 *  skip the colon so a 4-digit type-through feels natural. */
export function cursorAfterDigit(digitIdx: number): number {
  switch (digitIdx) {
    case 0: return 1;
    case 1: return 3; // jump past the colon
    case 2: return 4;
    case 3: return 5;
    default: return 5;
  }
}

/** Cursor position that lands ON `digitIdx` (i.e. caret immediately before
 *  that digit). Used by Backspace to position the caret over the just-
 *  zeroed digit. */
export function cursorAtDigit(digitIdx: number): number {
  switch (digitIdx) {
    case 0: return 0;
    case 1: return 1;
    case 2: return 3; // skip the colon
    case 3: return 4;
    default: return 0;
  }
}

/** Backspace at `cursor` chooses which digit to zero out and where the
 *  caret should land afterwards. Returns null when nothing to do (cursor
 *  already at the start). */
export function backspaceAtCursor(
  digits: string,
  cursor: number,
): { digits: string; cursor: number } | null {
  if (cursor <= 0) return null;
  let digitIdx: number;
  if (cursor === 1) digitIdx = 0;
  else if (cursor === 2) digitIdx = 1;
  else if (cursor === 3) digitIdx = 1; // caret on the colon → step back to last hour digit
  else if (cursor === 4) digitIdx = 2;
  else digitIdx = 3;
  const padded = digits.padEnd(4, '0').slice(0, 4);
  const next = padded.slice(0, digitIdx) + '0' + padded.slice(digitIdx + 1);
  return { digits: next, cursor: cursorAtDigit(digitIdx) };
}

/** Type a digit at `cursor` — replaces the targeted digit and advances the
 *  caret past the colon when crossing into minutes. Returns null when the
 *  cursor is past the last digit (no overwrite target). */
export function typeDigitAtCursor(
  digits: string,
  cursor: number,
  ch: string,
): { digits: string; cursor: number } | null {
  if (!/^\d$/.test(ch)) return null;
  // Snap a caret resting on the colon (cursor=2) over to the first
  // minute-digit slot. Past-end (cursor=5) snaps back to the last digit.
  let effective = cursor;
  if (effective === 2) effective = 3;
  if (effective >= 5) effective = 4;
  const digitIdx = digitIndexAtCursor(effective);
  if (digitIdx === null || digitIdx > 3) return null;
  const padded = digits.padEnd(4, '0').slice(0, 4);
  const next = padded.slice(0, digitIdx) + ch + padded.slice(digitIdx + 1);
  return { digits: next, cursor: cursorAfterDigit(digitIdx) };
}

// ---------- Diff inference (for onChangeText-driven editing) ----------

export type InferredEdit =
  | { kind: 'insert'; char: string }
  | { kind: 'delete' }
  | { kind: 'replace'; char: string }
  | { kind: 'clear' }
  | { kind: 'noop' };

/**
 * Classify the change between `oldText` and `newText` as a single editing
 * operation. iOS Safari soft keyboards don't fire `keydown` for character
 * input, so we infer the operation from the controlled-input diff instead
 * of intercepting key events.
 *
 * The caller is expected to apply our positional rules (overwrite at cursor,
 * skip the colon, zero-fill on backspace) using `oldCursor` — this helper
 * only reports WHAT happened, not HOW to apply it.
 */
export function inferEdit(oldText: string, newText: string): InferredEdit {
  if (newText === oldText) return { kind: 'noop' };
  if (newText === '') return { kind: 'clear' };

  // Insert: new text is one char longer; the inserted char is the first
  // character that differs when scanning from the start, OR (if the prefix
  // is fully identical) the trailing char.
  if (newText.length === oldText.length + 1) {
    let i = 0;
    while (i < oldText.length && oldText[i] === newText[i]) i++;
    const char = newText[i] ?? '';
    if (!/^\d$/.test(char)) return { kind: 'noop' };
    return { kind: 'insert', char };
  }

  // Delete: new text is one char shorter.
  if (newText.length === oldText.length - 1) {
    return { kind: 'delete' };
  }

  // Same length, different content — typically the user selected one digit
  // and replaced it (autocorrect, double-tap-to-select then type, etc.).
  if (newText.length === oldText.length) {
    let i = 0;
    while (i < newText.length && oldText[i] === newText[i]) i++;
    const char = newText[i] ?? '';
    if (!/^\d$/.test(char)) return { kind: 'noop' };
    return { kind: 'replace', char };
  }

  // Larger inserts/deletes (paste, multi-char delete) — treat as no-op so
  // the controlled input snaps back to the canonical mask on next render.
  return { kind: 'noop' };
}
